<script setup lang="ts">
import { computed, onMounted, ref, nextTick } from 'vue'
import Reload from '@/assets/icons/reload.svg'
import CommunityCardItem from './CommunityCardItem.vue'
import PlayerActionItem from './PlayerActionItem.vue'
import { storeToRefs } from 'pinia'
import { useGameState } from '@/stores/gameState'
import { watch } from 'vue'
import type { Action } from '@acepoker/contract/action'
import { cn } from '@/lib/utils'
import { useRouter } from 'vue-router'
import { assert } from '@/helpers/assert'
import { copyToClipboard } from '@/helpers/copyToClipboard'

const props = defineProps<{
  handHistory: Action[]
  class?: string
}>()

const topBarRef = ref()
const isAtStart = ref(false)
const isAtEnd = ref(true)
const router = useRouter()

onMounted(() => {
  if (topBarRef.value) {
    requestAnimationFrame(() => {
      topBarRef.value.scrollLeft = topBarRef.value?.scrollWidth
      topBarRef.value.blur()
    })
  }
})

watch(
  () => props.handHistory.length,
  () => {
    if (!topBarRef.value || !isAtEnd.value) return

    nextTick(() => {
      topBarRef.value.scrollTo({
        left: topBarRef.value.scrollWidth,
        behavior: 'smooth'
      })
    })
  }
)

const onScroll = (e: Event) => {
  // @ts-ignore
  isAtStart.value = e.target?.scrollLeft === 0 ? true : false

  const scrollAccuracy = 10
  isAtEnd.value =
    // @ts-ignore
    e.target?.scrollLeft + topBarRef.value.clientWidth + scrollAccuracy >
    topBarRef.value.scrollWidth
      ? true
      : false
}

const onClick = () => {
  const drill = props.handHistory
    .filter(({ type }) => type === 'action')
    .map((action) => {
      assert(action.type === 'action')
      let name = action.action === 'check' ? 'x' : action.action.charAt(0)
      if (name === 'r' || name === 'b') {
        name += `${action.totalBet}` || ''
      }
      return name
    })
    .join('')
  copyToClipboard(`/debug/${drill}`)
}
</script>

<template>
  <div
    ref="topBarRef"
    @scroll="onScroll"
    @pointerdown.stop
    @pointermove.stop
    @pointerup.stop
    :class="
      cn(
        {
          [`
        topbar
        flex
        flex-row 
        self-start 
        overflow-x-scroll 
        overflow-y-hidden
        w-[calc(100%-20px)] 
        items-center
        gap-2
        border-none!`]: true,
          'shadow-left': !isAtStart,
          'shadow-right': !isAtEnd
        },
        props.class
      )
    "
    @click="onClick"
  >
    <div v-for="(item, index) in props.handHistory" :key="index">
      <button class="topbar__item">
        <player-action-item
          v-if="item.type === 'action'"
          :item="item"
          :isLast="index === props.handHistory.length - 1"
        />
        <community-card-item v-else :item="item" />
      </button>
    </div>
    <!-- <button class="opacity-0" v-if="roomState.shortHistory.length !== 0">
      <component :is="Reload" width="24px" height="24px" alt="quality" />
    </button> -->
  </div>
</template>

<style lang="scss" scoped>
.topbar {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &__item {
    transform: translateZ(0);
  }
}

.shadow-left {
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 31px;
    width: 65px;
    pointer-events: none;
    background: linear-gradient(to right, rgba(23, 23, 27, 0.8), rgba(0, 0, 0, 0));
    z-index: 11;
  }
}

.shadow-right {
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 10px;
    height: 31px;
    width: 65px;
    pointer-events: none;
    background: linear-gradient(to left, rgba(23, 23, 27, 0.8), rgba(0, 0, 0, 0));
    z-index: 11;
  }
}
</style>
